import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  ViewEncapsulation,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, RouterModule } from '@angular/router';
import { HttpClientModule } from '@angular/common/http';

import { CareerJob, CareerService } from './career.service';
import { CanonicalService } from '../../../shared/seo/canonical.service';
import { ProblemserviceService } from '../problem-statement/problemservice.service';

/* =========================
   INTERFACES
========================= */

interface SectionCard {
  iconClass: string;
  title: string;
  description: string;
}

interface Testimonial {
  image: string;
  message: string;
  name: string;
}

interface ProblemItem {
  id: number | string;
  title: string;
}

interface ProblemTab {
  id: string;
  label: string;
}

@Component({
  selector: 'app-career',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    HttpClientModule
  ],
  templateUrl: './career.component.html',
  styleUrls: ['./career.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CareerComponent implements OnInit, AfterViewInit, OnDestroy {

  /* =====================================================
     JOBS
  ====================================================== */

  tabs: { key: string; label: string }[] = [];

  jobs: { [key: string]: CareerJob[] } = {};

  activeTab = '';

  @ViewChild('tabsWrapper')
  tabsWrapper!: ElementRef<HTMLDivElement>;


  /* =====================================================
     TAB SCROLL STATE
  ====================================================== */

  showLeftArrow = false;

  showRightArrow = false;

  private tabsCheckTimeout: ReturnType<typeof setTimeout> | null = null;


  /* =====================================================
     STATIC CONTENT
  ====================================================== */

  secCards: SectionCard[] = [];

  whatWeDoCards: any[] = [];

  testimonials: Testimonial[] = [];


  /* =====================================================
     INTERNSHIPS
  ====================================================== */

  internships: any[] = [];

  showInternshipSection = true;


  /* =====================================================
     PROBLEM STATEMENTS
  ====================================================== */

  tabsProblemTabs: ProblemTab[] = [];

  tabsProblemData: { [key: string]: ProblemItem[] } = {};

  tabsProblemActiveTab = '';


  @ViewChild('pillTabs')
  pillTabs!: ElementRef<HTMLDivElement>;


  /* =====================================================
     PROBLEM STATEMENT LIST
  ====================================================== */

  get tabsProblemList(): ProblemItem[] {
    return this.tabsProblemData[this.tabsProblemActiveTab] || [];
  }


  /* =====================================================
     CONSTRUCTOR
  ====================================================== */

  constructor(
    private title: Title,
    private meta: Meta,
    private careerService: CareerService,
    private canonicalService: CanonicalService,
    private problemservice: ProblemserviceService,
    private router: Router
  ) {}


  /* =====================================================
     LIFECYCLE
  ====================================================== */

  ngOnInit(): void {

    this.setPageMeta();

    this.loadJobs();

    this.initSectionCards();

    this.initWhatWeDoCards();

    this.initTestimonials();

    this.loadProblemStatements();

    this.loadInternships();
  }


  /* =====================================================
     AFTER VIEW INIT
  ====================================================== */

  ngAfterViewInit(): void {

    this.tabsCheckTimeout = setTimeout(() => {
      this.checkTabsOverflow();
    }, 100);
  }


  /* =====================================================
     DESTROY
  ====================================================== */

  ngOnDestroy(): void {

    if (this.tabsCheckTimeout) {

      clearTimeout(this.tabsCheckTimeout);

      this.tabsCheckTimeout = null;
    }

    this.showLeftArrow = false;

    this.showRightArrow = false;
  }


  /* =====================================================
     WINDOW RESIZE
  ====================================================== */

  @HostListener('window:resize')
  onWindowResize(): void {

    this.checkTabsOverflow();
  }


  /* =====================================================
     SEO
  ====================================================== */

  private setPageMeta(): void {

    this.title.setTitle(
      'Careers at MALtech | Join Our IT Team'
    );

    this.meta.updateTag({
      name: 'description',
      content:
        'Explore IT career opportunities at MALtech Solutions.'
    });

    this.canonicalService.setCanonicalURL('careers');
  }


  /* =====================================================
     JOBS
  ====================================================== */

  loadJobs(): void {

    this.careerService.getJobs().subscribe({

      next: (data: CareerJob[]) => {

        const grouped: {
          [key: string]: CareerJob[];
        } = {};


        data.forEach(job => {

          const cat =
            job.position_category?.trim() || 'General';

          grouped[cat] ??= [];

          grouped[cat].push(job);
        });


        this.jobs = grouped;


        this.tabs = Object.keys(grouped).map(key => ({
          key: key,
          label: key
        }));


        this.activeTab =
          this.tabs[0]?.key || '';


        /*
         * Wait for Angular to render the
         * dynamically created tabs.
         */
        this.scheduleTabsOverflowCheck();
      },


      error: () => {

        this.jobs = {};

        this.tabs = [];

        this.activeTab = '';

        this.showLeftArrow = false;

        this.showRightArrow = false;
      }
    });
  }


  /* =====================================================
     SET ACTIVE JOB TAB
  ====================================================== */

  setActiveTab(key: string): void {

    this.activeTab = key;
  }


  /* =====================================================
     CAREER SECTION SCROLL
  ====================================================== */

  Career(): void {

    document
      .getElementById('open-roles')
      ?.scrollIntoView({
        behavior: 'smooth'
      });
  }


  /* =====================================================
     TAB OVERFLOW CHECK
  ====================================================== */

  checkTabsOverflow(): void {

    if (!this.tabsWrapper) {

      return;
    }


    const el =
      this.tabsWrapper.nativeElement;


    /*
     * Check whether the tabs actually
     * exceed the available width.
     */
    const hasOverflow =
      el.scrollWidth >
      el.clientWidth + 5;


    /*
     * No overflow:
     * hide both arrows.
     */
    if (!hasOverflow) {

      this.showLeftArrow = false;

      this.showRightArrow = false;

      return;
    }


    /*
     * Overflow exists.
     *
     * Show left arrow only after
     * scrolling away from beginning.
     */
    this.showLeftArrow =
      el.scrollLeft > 5;


    /*
     * Show right arrow until the
     * end of the tabs is reached.
     */
    this.showRightArrow =
      el.scrollLeft + el.clientWidth <
      el.scrollWidth - 5;
  }


  /* =====================================================
     SCHEDULE OVERFLOW CHECK
  ====================================================== */

  private scheduleTabsOverflowCheck(): void {

    /*
     * Clear previous timeout
     * before creating another one.
     */
    if (this.tabsCheckTimeout) {

      clearTimeout(this.tabsCheckTimeout);
    }


    this.tabsCheckTimeout = setTimeout(() => {

      this.checkTabsOverflow();

      this.tabsCheckTimeout = null;

    }, 100);
  }


  /* =====================================================
     TAB SCROLL EVENT
  ====================================================== */

  onTabsScroll(): void {

    this.checkTabsOverflow();
  }


  /* =====================================================
     SCROLL TABS
  ====================================================== */

  scrollTabs(
    direction: 'left' | 'right'
  ): void {

    if (!this.tabsWrapper) {

      return;
    }


    const el =
      this.tabsWrapper.nativeElement;


    const scrollAmount = 250;


    el.scrollBy({

      left:
        direction === 'left'
          ? -scrollAmount
          : scrollAmount,

      behavior: 'smooth'
    });


    /*
     * Wait for smooth scrolling to finish
     * and update arrow visibility.
     */
    if (this.tabsCheckTimeout) {

      clearTimeout(this.tabsCheckTimeout);
    }


    this.tabsCheckTimeout = setTimeout(() => {

      this.checkTabsOverflow();

      this.tabsCheckTimeout = null;

    }, 350);
  }


  /* =====================================================
     WHY JOIN
  ====================================================== */

  private initSectionCards(): void {

    this.secCards = [

      {
        iconClass: 'bi-globe-americas',
        title: 'Global Exposure',
        description:
          'Work with international clients and diverse teams.'
      },

      {
        iconClass: 'bi-cpu',
        title: 'Cutting-Edge Tech',
        description:
          'Projects in AI, Cloud, DevOps and emerging tech.'
      },

      {
        iconClass: 'bi-people',
        title: 'Inclusive Culture',
        description:
          'Diversity, collaboration, and equal growth opportunities.'
      },

      {
        iconClass: 'bi-bar-chart',
        title: 'Career Growth',
        description:
          'Structured training, certifications and mentorship.'
      },

      {
        iconClass: 'bi-flask',
        title: 'Innovation Labs',
        description:
          'Innovate in dedicated labs with cutting-edge tech.'
      },

      {
        iconClass: 'bi-trophy',
        title: 'Recognition & Rewards',
        description:
          'Performance incentives and recognition.'
      }
    ];
  }


  /* =====================================================
     WHAT WE DO
  ====================================================== */

  private initWhatWeDoCards(): void {

    this.whatWeDoCards = [

      {
        image: 'assets/images/tech1.webp',
        title: 'Software Development',
        description:
          'Crafting innovative software solutions.'
      },

      {
        image: 'assets/images/tech2.webp',
        title: 'Software Consulting',
        description:
          'Optimize your software strategy.'
      },

      {
        image: 'assets/images/tech3.webp',
        title: 'UX Design',
        description:
          'User-centered design.'
      },

      {
        image: 'assets/images/tech4.webp',
        title: 'Digital Marketing',
        description:
          'Enhance your online presence.'
      },

      {
        image: 'assets/images/tech5.webp',
        title: 'AI & ML',
        description:
          'Intelligent systems.'
      },

      {
        image: 'assets/images/tech6.webp',
        title: 'Quantum Computing',
        description:
          'Next-gen computing.'
      }
    ];
  }


  /* =====================================================
     TESTIMONIALS
  ====================================================== */

  private initTestimonials(): void {

    this.testimonials = [

      {
        image: 'assets/images/testimonial.webp',
        message:
          'Supportive environment.',
        name:
          'Sarah – Software Engineer'
      },

      {
        image: 'assets/images/testimonial.webp',
        message:
          'Great collaboration.',
        name:
          'James – DevOps Engineer'
      },

      {
        image: 'assets/images/testimonial.webp',
        message:
          'Growth opportunities.',
        name:
          'Priya – Cloud Consultant'
      }
    ];
  }


  /* =====================================================
     SCROLL TO SECTION
  ====================================================== */

  scrollToSection(
    event: Event,
    id: string
  ): void {

    event.preventDefault();

    document
      .getElementById(id)
      ?.scrollIntoView({
        behavior: 'smooth'
      });
  }


  /* =====================================================
     PROBLEM STATEMENTS
  ====================================================== */

  loadProblemStatements(): void {

    this.problemservice.getProblemstatement().subscribe({

      next: (res: any) => {

        const list =
          Array.isArray(res)
            ? res
            : res.data || [];


        const valid =
          list.filter(
            (x: any) =>
              x.problem_title &&
              x.problem_category
          );


        this.tabsProblemData =
          this.groupProblems(valid);


        this.tabsProblemTabs =
          Object.keys(
            this.tabsProblemData
          ).map(key => ({

            id: key,

            label:
              key
                .replace(/_/g, ' ')
                .toUpperCase()
          }));


        this.tabsProblemActiveTab =
          this.tabsProblemTabs[0]?.id || '';


        /*
         * Keep existing problem statement
         * tab overflow logic.
         */
        this.scheduleTabsOverflowCheck();
      },


      error: () => {

        this.setFallbackProblemData();
      }
    });
  }


  /* =====================================================
     GROUP PROBLEM STATEMENTS
  ====================================================== */

  private groupProblems(items: any[]): {
    [key: string]: ProblemItem[]
  } {

    const map: {
      [key: string]: ProblemItem[]
    } = {};


    items.forEach(item => {

      const key =
        (item.problem_category || 'other')
          .toLowerCase()
          .replace(/\s+/g, '_');


      map[key] ??= [];


      map[key].push({

        id: item.id,

        title: item.problem_title
      });
    });


    return map;
  }


  /* =====================================================
     FALLBACK PROBLEM DATA
  ====================================================== */

  private setFallbackProblemData(): void {

    this.tabsProblemData = {

      smtp: [

        {
          id: 1,
          title:
            'AI-powered pre-send spam prediction'
        },

        {
          id: 2,
          title:
            'Intelligent Retry & Backoff'
        }
      ]
    };


    this.tabsProblemTabs = [

      {
        id: 'smtp',
        label: 'SMTP'
      }
    ];


    this.tabsProblemActiveTab = 'smtp';
  }


  /* =====================================================
     PROBLEM TAB SWITCH
  ====================================================== */

  tabsProblemSwitchTab(id: string): void {

    this.tabsProblemActiveTab = id;
  }


  /* =====================================================
     NAVIGATE TO PROBLEM
  ====================================================== */

  navigateToProblem(
    event: MouseEvent,
    id: any
  ): void {

    event.preventDefault();


    if (id) {

      this.router.navigate(
        ['/problem-statement'],
        {
          queryParams: {
            problem_id: id
          }
        }
      );
    }
  }


  /* =====================================================
     EXISTING PILL TAB SCROLL
  ====================================================== */

  private getTabWidth(): number {

    if (!this.pillTabs) {

      return 200;
    }


    const firstTab =
      this.pillTabs.nativeElement.querySelector(
        '.pill-tab'
      ) as HTMLElement;


    return firstTab?.offsetWidth || 200;
  }


  /* =====================================================
     PILL SCROLL RIGHT
  ====================================================== */

  scrollTabsRight(): void {

    if (!this.pillTabs) {

      return;
    }


    const width =
      this.getTabWidth();


    this.pillTabs.nativeElement.scrollBy({

      left: width,

      behavior: 'smooth'
    });
  }


  /* =====================================================
     PILL SCROLL LEFT
  ====================================================== */

  scrollTabsLeft(): void {

    if (!this.pillTabs) {

      return;
    }


    const width =
      this.getTabWidth();


    this.pillTabs.nativeElement.scrollBy({

      left: -width,

      behavior: 'smooth'
    });
  }


  /* =====================================================
     INTERNSHIPS
  ====================================================== */

  loadInternships(): void {

    this.internships = [

      {
        title: 'Programming',
        qualification:
          'B.Tech / M.Tech above 70%',
        btnText: 'Apply',
        route:
          '/internship/programming/'
      },

      {
        title: 'Non-Programming',
        qualification:
          'B.Tech / M.Tech above 70%',
        btnText: 'Apply',
        route:
          '/internship/non-programming/'
      }
    ];


    this.showInternshipSection = true;
  }

}